import React, { useCallback, useEffect, useState } from "react";
import { Dialog } from "@mui/material";
import CompanyLogo from "../../../../Full.logo.navy.png";
import WelcomeModal from "../WelcomeModal";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useDispatch } from "react-redux";
import RepositoryGuide from "./components/RepositoryGuide";
import { useSelector } from "react-redux";
import GuideNavigation from "./components/GuideNavigation";
import IntroductionGuide from "./components/IntroductionGuide";
import LeadClosingGuide from "./components/LeadClosingGuide";
import PerformanceGuide from "./components/PerformanceGuide";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TeamSettingsGuide from "./components/TeamSettingsGuide";
import LeadOverviewGuide from "./components/LeadOverviewGuide";
import ConfigurationGuide from "./components/ConfigurationGuide";
import NotificationsGuide from "./components/NotificationsGuide";
import PropertySearchGuide from "./components/PropertySearchGuide";
import ReportGenerationGuide from "./components/ReportGenerationGuide";
import LeadInvestigationGuide from "./components/LeadInvestigationGuide";
import { selectGuideNavSelection, selectShowWelcomeModal, setGuideNavSelection, setShowWelcomeModal } from "../../../../slices/sessionDataSlice";
import {
  GuideArticleScroll,
  GuideBrandLockup,
  GuideEyebrow,
  GuidePageButton,
  GuidePager,
  GuideReader,
  GuideReaderHeader,
  GuideReaderSubtitle,
  GuideReaderTitle,
  GuideSubjectHeader,
  GuideSubjectPanel,
  GuideTitle,
  GuideWorkspace,
} from "./components/GuideWorkspaceComponents";

const guideArticleTitles = {
  introduction: "Introduction",
  leadOverview: "Investigation Management",
  investigatingLeads: "Investigations",
  closingLeads: "Closing Investigations",
  performance: "Performance Metrics",
  repository: "Mortgage Repository",
  configuration: "System Configuration",
  propertySearch: "Property Search",
  reports: "Report Generation",
  notifications: "Notification System",
  settings: "App Settings",
};

const Guide = () => {
  const dispatch = useDispatch()
  
  const navSelection = useSelector(selectGuideNavSelection)

  const [forwardArticle, setForwardArticle] = useState("Overview")
  const [backwardArticle, setBackwardArticle] = useState(null)
  const showWelcomeModal = useSelector(selectShowWelcomeModal)

  const handleGuideNavSelection = useCallback((dir) => {
    if (navSelection === 'introduction') {
      if (dir === 'fwd') {
        dispatch( setGuideNavSelection('leadOverview'))
      }
      setForwardArticle("Investigation Management")
      setBackwardArticle(null)
    } else if (navSelection === 'leadOverview') {
      if (dir === 'fwd') {
        dispatch( setGuideNavSelection('investigatingLeads'))
      } else if (dir === 'bck') {
        dispatch( setGuideNavSelection('introduction'))
      }
      setForwardArticle("Investigations")
      setBackwardArticle("Introduction")
    } else if (navSelection === 'investigatingLeads') {
      if (dir === 'fwd') {
        dispatch( setGuideNavSelection('closingLeads'))
      } else if (dir === 'bck') {
        dispatch( setGuideNavSelection('leadOverview'))
      }
      setForwardArticle("Closing Investigations")
      setBackwardArticle("Overview")
    } else if (navSelection === 'closingLeads') {
      if (dir === 'fwd') {
        dispatch( setGuideNavSelection('performance'))
      } else if (dir === 'bck') {
        dispatch( setGuideNavSelection('investigatingLeads'))
      }
      setForwardArticle("Performance Metrics")
      setBackwardArticle("Investigations")
    } else if (navSelection === 'performance') {
      if (dir === 'fwd') {
        dispatch( setGuideNavSelection('repository'))
      } else if (dir === 'bck') {
        dispatch( setGuideNavSelection('closingLeads'))
      }
      setForwardArticle("Mortgage Repository")
      setBackwardArticle("Closing Leads")
    } else if (navSelection === 'repository') {
      if (dir === 'fwd') {
        dispatch( setGuideNavSelection('configuration'))
      } else if (dir === 'bck') {
        dispatch( setGuideNavSelection('performance'))
      }
      setForwardArticle("System Configuration")
      setBackwardArticle("Performance Metrics")
    } else if (navSelection === 'configuration') {
      if (dir === 'fwd') {
        dispatch( setGuideNavSelection('propertySearch'))
      } else if (dir === 'bck') {
        dispatch( setGuideNavSelection('repository'))
      }
      setForwardArticle("Property Search")
      setBackwardArticle("Mortgage Repository")
    } else if (navSelection === 'propertySearch') {
      if (dir === 'fwd') {
        dispatch( setGuideNavSelection('reports'))
      } else if (dir === 'bck') {
        dispatch( setGuideNavSelection('configuration'))
      }
      setForwardArticle("Report Generation")
      setBackwardArticle("System Configuration")
    } else if (navSelection === 'reports') {
      if (dir === 'fwd') {
        dispatch( setGuideNavSelection('notifications'))
      } else if (dir === 'bck') {
        dispatch( setGuideNavSelection('propertySearch'))
      }
      setForwardArticle("Notification System")
      setBackwardArticle("Property Search")
    } else if (navSelection === 'notifications') {
      if (dir === 'fwd') {
        dispatch( setGuideNavSelection('settings'))
      } else if (dir === 'bck') {
        dispatch( setGuideNavSelection('reports'))
      }
      setForwardArticle("App Settings")
      setBackwardArticle("Report Generation")
    } else if (navSelection === 'settings') {
      if (dir === 'bck') {
        dispatch( setGuideNavSelection('notifications'))
      }
      setForwardArticle(null)
      setBackwardArticle("Notification System")
    }
  }, [navSelection, dispatch])

  useEffect(() => {
    handleGuideNavSelection(null)
  }, [handleGuideNavSelection, navSelection])

  const currentArticleTitle = guideArticleTitles[navSelection] || "App Settings";

  return (
    <>
      <GuideWorkspace>
        <GuideSubjectPanel>
          <GuideSubjectHeader>
            <GuideEyebrow>Guide</GuideEyebrow>
            <GuideTitle>Subjects</GuideTitle>
          </GuideSubjectHeader>
          <GuideNavigation navSelection={navSelection} />
        </GuideSubjectPanel>

        <GuideReader>
          <GuideReaderHeader>
            <GuideBrandLockup>
              <img alt="lancaster sweep guide" src={CompanyLogo} />
              <div>
                <GuideReaderSubtitle>Information Guide</GuideReaderSubtitle>
                <GuideReaderTitle>{currentArticleTitle}</GuideReaderTitle>
              </div>
            </GuideBrandLockup>

            <GuidePager aria-label="Guide article navigation">
              <GuidePageButton
                type="button"
                disabled={!backwardArticle}
                onClick={backwardArticle ? () => handleGuideNavSelection('bck') : undefined}
              >
                <ArrowBackIcon />
                {backwardArticle || "Previous"}
              </GuidePageButton>
              <GuidePageButton
                type="button"
                disabled={!forwardArticle}
                onClick={forwardArticle ? () => handleGuideNavSelection('fwd') : undefined}
              >
                {forwardArticle || "Next"}
                <ArrowForwardIcon />
              </GuidePageButton>
            </GuidePager>
          </GuideReaderHeader>

          <GuideArticleScroll>
            {navSelection === 'introduction' ?
              <IntroductionGuide />
            : navSelection === 'leadOverview' ?
              <LeadOverviewGuide />
            : navSelection === 'investigatingLeads' ?
              <LeadInvestigationGuide />
            : navSelection === 'closingLeads' ?
              <LeadClosingGuide />
            : navSelection === 'performance' ?
              <PerformanceGuide />
            : navSelection === 'repository' ?
              <RepositoryGuide />
            : navSelection === 'configuration' ?
              <ConfigurationGuide />
            : navSelection === 'propertySearch' ?
              <PropertySearchGuide />
            : navSelection === 'reports' ?
              <ReportGenerationGuide />
            : navSelection === 'notifications' ?
              <NotificationsGuide />
            :
              <TeamSettingsGuide />
            }
          </GuideArticleScroll>
        </GuideReader>
      </GuideWorkspace>

      <Dialog 
        open={showWelcomeModal}
        onClose={() => dispatch( setShowWelcomeModal(false) )}
        PaperProps={{ sx: {width: "70rem", minWidth: "70rem", maxWidth: "70rem", height: "30rem", minHeight: "30rem", maxHeight: "30rem"}}}
      >
        <WelcomeModal />
      </Dialog>
    </>
  )
}

export default Guide
